// The pieces both screens share: async state, error rendering, and the modal
// that every write in this app goes through.
//
// Extracted from screens.tsx when Stage B added a second screen with a second
// table. Copying `ErrorState` and `useAsync` into requests.tsx would have been
// two lines of work and a permanent second source of truth for how this console
// reports a failure — which is the one behaviour that must not vary by screen.

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "./api.js";

/** Map a registry status onto the shared `.pill` variants. No new colours. */
export function statusPill(status: string): string {
  switch (status) {
    case "active":
    case "approved":
      return "pill accepted";
    case "provisioning":
    case "approving":
      return "pill proposed";
    case "failed":
    case "rejected":
      return "pill error";
    case "suspended":
      return "pill issues";
    default:
      return "pill pending"; // 'draft', 'pending', 'spam'
  }
}

export function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  // Locale-independent and unambiguous. A console read in two countries should
  // not render 03/04 as two different days.
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Date AND time — for a queue, "when did this arrive" is a different question. */
export function fmtDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${fmtDate(iso)}, ${d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

/**
 * One error renderer for every screen.
 *
 * The 503 branch matters most: `main` auto-deploys on merge while migrations and
 * role grants are applied by hand, so "the console is live but its database
 * objects are not" is a NORMAL state for a while — and it must read as that,
 * with the remedy, not as a crash.
 */
export function ErrorState({ err, onRetry }: { err: ApiError; onRetry: () => void }) {
  return (
    <div className="card" style={{ padding: 20, maxWidth: 620 }}>
      <div
        className={err.notProvisioned ? "pill issues" : "pill error"}
        style={{ marginBottom: 10 }}
      >
        {/* The gateway's own heading when it sends one — four different 403s
            used to arrive under one generic title. */}
        {err.title ?? (err.notProvisioned ? "Not set up in this environment" : `Error ${err.status || ""}`)}
      </div>
      <p style={{ margin: "0 0 12px", fontSize: 13, lineHeight: 1.55, color: "var(--text)" }}>
        {err.message}
      </p>
      {err.code === "session_expired" ? (
        <p style={{ margin: "0 0 12px", fontSize: 12.5, color: "var(--text-muted)" }}>
          Reload the page to sign in again.
        </p>
      ) : null}
      <button className="btn sm focusable" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

export function Loading({ label }: { label: string }) {
  return (
    <div className="card" style={{ padding: 20 }}>
      <div
        className="shimmer"
        style={{ height: 12, width: 180, borderRadius: "var(--radius-sm)" }}
      />
      <p style={{ margin: "12px 0 0", fontSize: 12.5, color: "var(--text-muted)" }}>{label}</p>
    </div>
  );
}

export function asApiError(err: unknown): ApiError {
  return err instanceof ApiError
    ? err
    : new ApiError(0, "unknown", err instanceof Error ? err.message : String(err));
}

/** A generic async-data hook, so every screen handles all three states. */
export function useAsync<T>(
  load: () => Promise<T>,
  deps: unknown[],
  /**
   * Keep the data current while the page is open, without a reload and without
   * touching the reporting period (Omar, 2026-09-16).
   *
   * ★ A QUIET REFRESH, NOT A RE-MOUNT. The dependency path below sets
   *   `{t:"loading"}` first, which blanks the panel — correct when the customer
   *   or the period changes, and wrong every 30 seconds. A poll that flashes an
   *   empty card is worse than no poll: the reader cannot tell a refresh from a
   *   failure. So the interval replaces `data` in place and leaves the previous
   *   figures on screen throughout.
   *
   * ★ AND A FAILED BACKGROUND REFRESH DOES NOT BLANK A WORKING SCREEN — but it
   *   must not be invisible either. `refreshedAt` stops advancing, and the
   *   caller prints it, so a screen that has quietly stopped updating says so
   *   with a timestamp rather than by looking fine
   *   (`feedback_absence_and_emptiness_read_the_same`).
   */
  opts?: { refreshMs?: number },
) {
  const [state, setState] = useState<
    { t: "loading" } | { t: "ok"; data: T } | { t: "err"; err: ApiError }
  >({ t: "loading" });
  const [nonce, setNonce] = useState(0);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  // The latest `load` without making it a dependency: a caller passing an inline
  // arrow (all of them do) would otherwise restart the interval on every render.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let live = true;
    setState({ t: "loading" });
    loadRef
      .current()
      .then((data) => {
        if (live) {
          setState({ t: "ok", data });
          setRefreshedAt(new Date());
        }
      })
      .catch((err: unknown) => {
        if (live) setState({ t: "err", err: asApiError(err) });
      });
    return () => {
      live = false;
    };
  }, [...deps, nonce]);

  const refreshMs = opts?.refreshMs ?? 0;
  useEffect(() => {
    if (refreshMs <= 0) return undefined;
    let live = true;
    let inFlight = false;
    const tick = () => {
      // ⚠️ Three guards, and each one is a real case. A hidden tab must not
      //    poll (a laptop left open overnight is 2,880 needless requests); a
      //    slow response must not stack another request behind it; and an
      //    unmounted component must not setState.
      if (!live || inFlight) return;
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      )
        return;
      inFlight = true;
      loadRef
        .current()
        .then((data) => {
          if (live) {
            setState({ t: "ok", data });
            setRefreshedAt(new Date());
          }
        })
        .catch(() => {
          // Deliberately silent HERE: the last good figures stay on screen and
          // `refreshedAt` stops moving, which is the honest signal. Replacing a
          // working panel with an error because one poll failed would make a
          // blip look like an outage.
        })
        .finally(() => {
          inFlight = false;
        });
    };
    const id = setInterval(tick, refreshMs);
    // Catch up immediately when the tab comes back, rather than waiting out the
    // rest of an interval that elapsed while it was hidden.
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    if (typeof document !== "undefined")
      document.addEventListener("visibilitychange", onVisible);
    return () => {
      live = false;
      clearInterval(id);
      if (typeof document !== "undefined")
        document.removeEventListener("visibilitychange", onVisible);
    };
  }, [...deps, nonce, refreshMs]);

  return { state, refreshedAt, retry: () => setNonce((n) => n + 1) };
}

/**
 * ★ THE LATCH. A `disabled` prop is not one.
 *
 * `setBusy(true)` schedules a re-render; it does not disable anything at the
 * moment of the click. Two clicks inside one frame — a double-click, a
 * trackpad's second contact, an impatient tap on a slow phone — both read the
 * pre-render value and both fire. On the approve button that is two Auth0
 * provisioning attempts and, if the row's own status lock ever moved or was
 * bypassed, two invitation emails to a real person.
 *
 * A ref is written and read SYNCHRONOUSLY, so the second click sees the first.
 * The `busy` state exists only to grey the button; it is not the guard.
 *
 * ⚠️ The latch is released ONLY on failure. After a success the caller has
 * already moved on to a result view or closed the dialog, and re-arming the
 * button in the window between the promise resolving and React repainting would
 * put the one dangerous click back on screen for a frame.
 */
export function useSubmitLatch() {
  const latched = useRef(false);
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const run = useCallback(
    <T,>(work: () => Promise<T>, onOk: (value: T) => void, onErr: (err: ApiError) => void) => {
      if (latched.current) return;
      latched.current = true;
      setBusy(true);
      void work().then(
        (value) => {
          if (!alive.current) return;
          setBusy(false);
          onOk(value);
        },
        (err: unknown) => {
          if (!alive.current) return;
          latched.current = false; // only here — see the note above
          setBusy(false);
          onErr(asApiError(err));
        },
      );
    },
    [],
  );

  return { run, busy };
}

/**
 * The dialog every write in this console goes through.
 *
 * ⚠️ `busy` does more than grey a button: while a write is in flight the dialog
 * refuses Escape and refuses a scrim click. Closing mid-approve would not cancel
 * anything — the server would finish, email the person, and the operator would
 * be looking at a queue that had not refreshed, with no idea whether it worked.
 */
export function Modal({
  title,
  onClose,
  busy,
  children,
  footer,
  wide,
}: {
  title: string;
  onClose: () => void;
  busy: boolean;
  children: React.ReactNode;
  footer: React.ReactNode;
  wide?: boolean;
}) {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  // Move focus into the dialog so the keyboard follows the eye, and so Escape
  // reaches the handler above rather than the page behind it.
  useEffect(() => {
    const first = panel.current?.querySelector<HTMLElement>(
      "input, textarea, select, button",
    );
    first?.focus();
  }, []);

  return (
    <div
      className="console-modal-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        className={wide ? "console-modal wide" : "console-modal"}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={panel}
      >
        <div className="console-modal-head">
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "var(--text-strong)" }}>
            {title}
          </h2>
        </div>
        <div className="console-modal-body">{children}</div>
        <div className="console-modal-foot">{footer}</div>
      </div>
    </div>
  );
}

/** A labelled form control. Layout only — the input carries `.field`. */
export function Labelled({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <span
        style={{
          display: "block",
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "var(--text-faint)",
          fontWeight: 600,
          marginBottom: 4,
        }}
      >
        {label}
      </span>
      {children}
      {hint ? (
        <span
          style={{
            display: "block",
            fontSize: 12,
            lineHeight: 1.5,
            color: "var(--text-muted)",
            marginTop: 4,
          }}
        >
          {hint}
        </span>
      ) : null}
    </label>
  );
}

export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 16 }}>
      <h2
        style={{
          margin: "0 0 6px",
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "var(--text-faint)",
          fontWeight: 600,
        }}
      >
        {title}
      </h2>
      <div className="card" style={{ padding: "4px 14px" }}>{children}</div>
    </section>
  );
}

export function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 14,
        padding: "8px 0",
        borderBottom: "1px solid var(--border-faint)",
        fontSize: 13,
        flexWrap: "wrap",
      }}
    >
      <span style={{ flex: "0 0 150px", color: "var(--text-muted)" }}>{label}</span>
      <span
        style={{
          flex: 1,
          minWidth: 140,
          color: "var(--text)",
          ...(mono ? { fontFamily: "var(--font-mono)", fontSize: 12 } : {}),
        }}
      >
        {value}
      </span>
    </div>
  );
}

/** The failure a write reports, inside the dialog that caused it. */
/**
 * A block of text shown EXACTLY as it is sent — a composed prompt, a ruleset.
 *
 * ★ ONE DEFINITION, because there are now two screens that show the operator
 * "here is the literal instruction the model receives" and a second copy of
 * these eleven properties would drift silently: the two boxes would stop looking
 * like the same kind of thing, and only one of them would carry the decision
 * below (`feedback_one_rule_one_place`).
 *
 * ★ NO `maxHeight` / `overflowY`. Omar, 2026-09-15: "Remove the vertical scroll
 *   for the prompts on all the tabs (i.e. Visual craft, etc)". A box that
 *   scrolls inside a page that also scrolls gives two scrollbars for one
 *   document and hides how long the instruction actually is; the prompt runs to
 *   its full length and the PAGE is the only thing that scrolls.
 *   `overflowX` stays: a single over-long line must not widen the page.
 */
export const CODE_STYLE: React.CSSProperties = {
  margin: 0,
  padding: "12px 14px",
  background: "var(--surface-inset)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
  fontFamily: "var(--font-mono)",
  fontSize: 11.5,
  lineHeight: 1.6,
  color: "var(--text)",
  whiteSpace: "pre-wrap",
  overflowX: "auto",
};

export function InlineError({ err }: { err: ApiError }) {
  return (
    <div
      className="card"
      style={{ padding: "10px 12px", marginBottom: 12, borderColor: "var(--error)" }}
    >
      <div className="pill error" style={{ marginBottom: 6 }}>
        {err.title ?? (err.status ? `Failed — ${err.status}` : "Failed")}
      </div>
      <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: "var(--text)" }}>
        {err.message}
      </p>
    </div>
  );
}
